const SERVICE_LINE_DX_SLOT_COUNT = 12;

/** Dotless, uppercased key for deduplication (periods stripped only). */
export function diagnosisCodeNormalizedKey(raw: string): string {
    return (raw || '').replace(/\./g, '').trim().toUpperCase();
}

/**
 * Canonical diagnosis-code format used across UI and claim payloads:
 * insert '.' after the third character when there is a fourth.
 */
export function formatDiagnosisCodeCanonical(dotlessUpper: string): string {
    const s = diagnosisCodeNormalizedKey(dotlessUpper);
    if (!s) return '';
    if (s.length <= 3) return s;
    return `${s.slice(0, 3)}.${s.slice(3)}`;
}

/** Backward-compatible alias for canonical diagnosis-code formatting. */
export function formatDiagnosisCodeForDisplay(dotlessUpper: string): string {
    return formatDiagnosisCodeCanonical(dotlessUpper);
}

/**
 * Strip dots, uppercase, dedupe (first occurrence wins), format with '.' after index 2,
 * pack into up to 12 slots (remainder empty strings).
 */
export function normalizeServiceLineDiagnosisCodes(
    codes: Array<string | null | undefined> | undefined | null,
): string[] {
    const input = Array.isArray(codes) ? codes : [];
    const seen = new Set<string>();
    const packed: string[] = [];
    for (const raw of input) {
        if (typeof raw !== 'string') continue;
        const key = diagnosisCodeNormalizedKey(raw);
        if (!key) continue;
        if (seen.has(key)) continue;
        seen.add(key);
        packed.push(formatDiagnosisCodeCanonical(key));
    }
    while (packed.length < SERVICE_LINE_DX_SLOT_COUNT) packed.push('');
    return packed.slice(0, SERVICE_LINE_DX_SLOT_COUNT);
}

/** Ordered unique formatted codes for API / claim payload (no trailing empty slots). */
export function normalizedDiagnosisCodesForPayload(
    codes: Array<string | null | undefined> | undefined | null,
): string[] {
    return normalizeServiceLineDiagnosisCodes(codes).filter(Boolean);
}
